import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { clearAccessToken, setAccessToken } from '../lib/setupAxiosAuth';

const AuthContext = createContext(null);

function formatApiError(detail) {
  if (detail == null) return 'Une erreur est survenue.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (item?.msg ? item.msg : JSON.stringify(item))).join(' ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setAuthState(response.data);
      return response.data;
    } catch (_error) {
      setAuthState(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      if (response.data.access_token) {
        setAccessToken(response.data.access_token);
      }
      setAuthState(response.data.user);
      return { ok: true, user: response.data.user };
    } catch (error) {
      return { ok: false, error: formatApiError(error.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } finally {
      clearAccessToken();
      setAuthState(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user: authState, isCheckingAuth: authState === null, login, logout, refreshUser }),
    [authState, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
